/*******************************************************************************
 * Copyright 2026 Collaterate. All rights reserved.
 *******************************************************************************/

// Storefront search-box typeahead for [[ semantic_search_typeahead ]].
//
// SAME-ORIGIN at config.restBase + '/search/typeahead', never the engine
// directly: its CORS allowlist is deployed config and every site has its own
// domain. The proxy attaches the context token server-side, so there is no token
// here and there must not be one -- that would be a bearer credential in source.
//
// SELECTING A PRODUCT ROW GOES TO THAT PRODUCT, using the engine's per-product
// link. A row whose link the URL guard rejected falls back to running the search,
// so a bad link degrades rather than dead-ends. The LAST row is "view all
// results", which keeps the old behavior, the no-JS form submit and the funnel's
// search_results_viewed. Both routes go through selectOption, so mouse and
// keyboard cannot drift apart.
//
// FACET ROWS ("Postcards" in "Product type") sit in a "Filters" section between the
// completions and the products. Choosing one goes to the search page filtered by that
// value (k=<label>&f.<group>=<value>&ix=1), or runs the plain search for the label when
// there is no search page to go to. Its selection detail gains facet: {group, value};
// every other row's detail is unchanged.
//
// THE PANEL FLOATS IN THE TOP LAYER where the browser has one (the Popover API), placed against
// the viewport under the search box. A header that clips its children (overflow: hidden at a fixed
// height, as the site 443 theme family's search container does) or sits in a low stacking context
// would otherwise hide the whole panel. Without the Popover API the panel stays where the theme's
// CSS puts it, as before.
//
// Selection raises SEARCH_TYPEAHEAD_SELECTED plus a
// collaterate:search:typeahead_select CustomEvent -- without it, going direct to
// a product makes every typeahead conversion invisible, since it no longer lands
// on a results page for search_results_viewed to stand in for.
//
// ES5 on purpose, matching the rest of this bundle's browser targets.

(function (window, document) {
    'use strict';

    var DEBOUNCE_MS = 300;
    /** Long enough for a row's own click to run after the blur it caused, short enough to feel instant. */
    var BLUR_CLOSE_MS = 150;
    var MIN_CHARS = 2;

    function config() {
        return window.searchTypeaheadConfig || null;
    }

    function logSuppressed(error) {
        try {
            if (window.console && window.console.debug) {
                window.console.debug('searchTypeahead suppressed an error', error);
            }
        } catch (ignored) {
            // Nothing further to do: a logging failure must not become a page failure.
        }
    }

    /**
     * Raises the pair: the storefront bus for theme controllers already listening on it, and a
     * namespaced CustomEvent for the payload, because the bus passes no arguments.
     *
     * Guarded on the CONSTANT, not just the bus: trigger() is gated on isEventRegistered, so a name
     * templateHeader.js never declared is a silent no-op, and dispatching a DOM event for an event
     * that cannot fire on the bus would be worse than silence.
     */
    function announceSelection(detail) {
        try {
            var bus = window.events;
            if (!bus || !bus.SEARCH_TYPEAHEAD_SELECTED) {
                return;
            }
            bus.trigger(bus.SEARCH_TYPEAHEAD_SELECTED);
            if (typeof window.CustomEvent === 'function') {
                window.dispatchEvent(new window.CustomEvent('collaterate:search:typeahead_select', {
                    detail: detail,
                }));
            }
        } catch (error) {
            // A theme's listener throwing must not stop the navigation the shopper asked for.
            logSuppressed(error);
        }
    }

    /** The box the panel hangs under: the tag's own wrapper, else whatever holds the input. */
    function anchorOf(input) {
        var node = input;
        while (node && node.getAttribute) {
            if (node.getAttribute('data-collaterate-typeahead') !== null) {
                return node;
            }
            node = node.parentNode;
        }
        return input.form || input.parentNode || input;
    }

    function init() {
        var cfg = config();
        if (!cfg) {
            // No config global means the tag is not on this page. Not an error.
            return;
        }
        var input = document.getElementById(cfg.inputId);
        var panel = document.getElementById(cfg.panelId);
        if (!input || !panel) {
            return;
        }
        // Absent from an older cached page or a theme's own markup: then nothing is announced, as before.
        var status = document.getElementById('searchTypeaheadStatus');

        /** Says how many rows a response produced. Once per response, never per keystroke. */
        function announceCount(text) {
            if (!status) {
                return;
            }
            try {
                status.textContent = text;
            } catch (error) {
                logSuppressed(error);
            }
        }

        /** The search page URL: the config's, else the form's own action. Empty when neither exists. */
        function searchUrl() {
            var cfgNow = config();
            if (cfgNow && cfgNow.searchUrl) {
                return cfgNow.searchUrl;
            }
            if (input.form && typeof input.form.getAttribute === 'function') {
                return input.form.getAttribute('action') || '';
            }
            return '';
        }

        var timer = null;
        // The deferred close scheduled by blur. Held so that refocusing before it runs can cancel it,
        // rather than letting a stale close tear down a panel the shopper has come back to.
        var blurTimer = null;
        // Monotonic request id: typing "fol" then "fold" can land the slower "fol" response LAST,
        // painting suggestions for a prefix already moved past. Debouncing reduces that and cannot
        // prevent it, so only the newest id may paint.
        var requestId = 0;
        var options = [];
        var activeIndex = -1;

        var anchor = anchorOf(input);
        var floats = typeof panel.showPopover === 'function' && typeof panel.hidePopover === 'function';
        // Whether THIS script showed the popover, so it is never shown twice or hidden when not shown.
        var floating = false;
        if (floats) {
            // manual, not auto: an auto popover light-dismisses on any outside click and closes other
            // popovers on the page. Open and close stay exactly where they were, in render and close.
            panel.setAttribute('popover', 'manual');
        }

        /** Puts a floating panel under the box. Called on open and on every scroll or resize while open. */
        function place() {
            if (!floating) {
                return;
            }
            try {
                var box = anchor.getBoundingClientRect();
                panel.style.top = box.bottom + 'px';
                panel.style.left = box.left + 'px';
                panel.style.width = box.width + 'px';
            } catch (error) {
                logSuppressed(error);
            }
        }

        function showPanel() {
            panel.hidden = false;
            if (floats && !floating) {
                try {
                    panel.showPopover();
                    floating = true;
                } catch (error) {
                    // A closed popover is display: none by the browser's own rules, so stop floating for good
                    // and drop the attribute: the panel is then where the theme's CSS puts it, the old behavior.
                    floats = false;
                    panel.removeAttribute('popover');
                    logSuppressed(error);
                }
            }
            place();
        }

        function hidePanel() {
            if (floating) {
                floating = false;
                try {
                    panel.hidePopover();
                } catch (error) {
                    logSuppressed(error);
                }
            }
            panel.hidden = true;
        }

        if (floats) {
            // Capture, so a scroll inside any container (not just the page) moves the panel with the box.
            window.addEventListener('scroll', place, true);
            window.addEventListener('resize', place);
        }

        function setState(name, on) {
            try {
                if (on) {
                    panel.classList.add(name);
                } else {
                    panel.classList.remove(name);
                }
            } catch (error) {
                logSuppressed(error);
            }
        }

        function close() {
            // Cancel the work still owed to the dismissed prefix, BEFORE clearing the DOM. Otherwise a
            // debounce timer set before Escape still fires, and a request in flight still passes the
            // supersession check and re-opens a panel the shopper just dismissed.
            if (timer) {
                window.clearTimeout(timer);
                timer = null;
            }
            requestId++;

            hidePanel();
            panel.innerHTML = '';
            options = [];
            activeIndex = -1;
            input.setAttribute('aria-expanded', 'false');
            input.removeAttribute('aria-activedescendant');
            setState('is-open', false);
            setState('is-loading', false);
            setState('is-empty', false);
            announceCount('');
        }

        // aria-activedescendant announces the highlighted row and aria-selected is what a theme
        // styles. Driven from one place so an announced row cannot differ from the styled one.
        function highlight(index) {
            activeIndex = index;
            for (var i = 0; i < options.length; i++) {
                var selected = i === index;
                options[i].setAttribute('aria-selected', selected ? 'true' : 'false');
            }
            if (index >= 0 && options[index]) {
                input.setAttribute('aria-activedescendant', options[index].id);
            } else {
                input.removeAttribute('aria-activedescendant');
            }
        }

        /**
         * The product name with the typed prefix wrapped in <b>.
         *
         * Built from text nodes, never innerHTML: a product name is engine data, and the whole reason
         * the old renderer used textContent was to keep it inert. Matching is case-insensitive and
         * done by INDEX rather than by regex, so a name containing regex metacharacters cannot change
         * what gets matched.
         */
        function nameWithPrefixMarked(name, prefix) {
            var fragment = document.createDocumentFragment();
            var at = name.toLowerCase().indexOf(prefix.toLowerCase());
            if (!prefix || at < 0) {
                fragment.appendChild(document.createTextNode(name));
                return fragment;
            }
            if (at > 0) {
                fragment.appendChild(document.createTextNode(name.slice(0, at)));
            }
            var marked = document.createElement('b');
            marked.textContent = name.slice(at, at + prefix.length);
            fragment.appendChild(marked);
            if (at + prefix.length < name.length) {
                fragment.appendChild(document.createTextNode(name.slice(at + prefix.length)));
            }
            return fragment;
        }

        /**
         * A section heading, with its action on the same line.
         *
         * role="presentation" because a listbox's children are options, and a heading is not one:
         * marked otherwise it would be announced as a selectable row and counted in the set size.
         * It is deliberately NOT in `options`, so arrow keys step over it.
         */
        function sectionHeading(label, action) {
            var heading = document.createElement('li');
            heading.className = 'c-search-typeahead-heading';
            heading.setAttribute('role', 'presentation');

            var text = document.createElement('span');
            text.className = 'c-search-typeahead-heading-label';
            text.textContent = label;
            heading.appendChild(text);

            if (action) {
                heading.appendChild(action);
            }
            return heading;
        }

        /** A row the click and keyboard handlers can both act on. */
        function newRow(index, className) {
            var row = document.createElement('li');
            row.id = 'searchTypeaheadOption' + index;
            row.className = className;
            row.setAttribute('role', 'option');
            row.setAttribute('aria-selected', 'false');
            row.setAttribute('data-typeahead-row', '1');
            return row;
        }

        /**
         * One query completion. A PHRASE, not a product: selecting it runs the search for that
         * phrase, so it carries no link and the results page is built by the form as always.
         */
        function suggestionRow(suggestion, index, prefix) {
            var row = newRow(index, 'c-search-typeahead-suggestion');
            var phrase = suggestion.phrase || '';
            row.setAttribute('data-suggestion', phrase);
            row.appendChild(nameWithPrefixMarked(phrase, prefix));
            return row;
        }

        /**
         * One facet value ("Postcards" in "Product type"). Selecting it goes to the search page filtered by that
         * value, so it carries the ids as attributes and never builds a URL here until selection time.
         */
        function facetRow(facet, index, prefix) {
            var row = newRow(index, 'c-search-typeahead-facet');
            row.setAttribute('data-facet-group', facet.group || '');
            row.setAttribute('data-facet-value', facet.value || '');
            row.setAttribute('data-facet-label', facet.label || '');

            var label = document.createElement('span');
            label.className = 'c-search-typeahead-facet-label';
            label.appendChild(nameWithPrefixMarked(facet.label || '', prefix));
            row.appendChild(label);

            var group = document.createElement('span');
            group.className = 'c-search-typeahead-facet-group';
            // A text node: the group label is engine data.
            group.appendChild(document.createTextNode(' \u00b7 ' + (facet.groupLabel || facet.group || '')));
            row.appendChild(group);
            return row;
        }

        function productRow(product, index, prefix) {
            var row = newRow(index, 'c-search-typeahead-option');
            row.setAttribute('data-product-id', product.id || '');
            var name = product.name || product.id || '';
            row.setAttribute('data-product-name', name);
            // An EMPTY link is a REJECTED url, not an absent one: safeUrl returns "" for anything that
            // is not absolute http(s) or genuinely same-origin. Such a row keeps the old behavior and
            // runs the search instead of navigating somewhere the guard refused.
            if (product.link) {
                row.setAttribute('data-product-link', product.link);
            }

            // Same rule for the thumbnail: absent OR rejected means render no <img> at all. An
            // <img src=""> re-requests the current page in some browsers, which is worse than no image.
            if (product.imageLink) {
                var thumb = document.createElement('img');
                thumb.className = 'c-search-typeahead-option-thumb';
                thumb.setAttribute('src', product.imageLink);
                // Decorative: the name sits beside it in the same row, so alt text would be read twice.
                thumb.setAttribute('alt', '');
                thumb.setAttribute('loading', 'lazy');
                row.appendChild(thumb);
            }

            var body = document.createElement('span');
            body.className = 'c-search-typeahead-option-body';

            var nameEl = document.createElement('span');
            nameEl.className = 'c-search-typeahead-option-name';
            nameEl.appendChild(nameWithPrefixMarked(name, prefix));
            body.appendChild(nameEl);

            if (product.description) {
                var desc = document.createElement('span');
                desc.className = 'c-search-typeahead-option-desc';
                desc.textContent = product.description;
                body.appendChild(desc);
            }

            row.appendChild(body);
            return row;
        }

        /**
         * The section's action: runs the search, which is what selecting anything used to do.
         *
         * Sits on the PRODUCTS heading row rather than at the foot of the list. It is still a
         * data-typeahead-row so one click handler covers it, and still in `options` so the keyboard
         * can reach it -- it is an action, not decoration.
         */
        function viewAllAction(index, prefix) {
            var action = document.createElement('span');
            action.id = 'searchTypeaheadOption' + index;
            action.className = 'c-search-typeahead-all';
            action.setAttribute('role', 'option');
            action.setAttribute('aria-selected', 'false');
            action.setAttribute('data-typeahead-row', '1');
            action.setAttribute('data-search-all', '1');
            action.textContent = 'View All';
            action.setAttribute('title', 'View all results for "' + prefix + '"');
            return action;
        }

        function render(products, suggestions, facets, prefix) {
            panel.innerHTML = '';
            options = [];
            activeIndex = -1;
            setState('is-loading', false);

            if (!products.length && !suggestions.length && !facets.length) {
                // A prefix matching nothing is an answer: leaving the previous suggestions up would
                // tell the shopper their new prefix matched them.
                hidePanel();
                input.setAttribute('aria-expanded', 'false');
                setState('is-open', false);
                setState('is-empty', true);
                announceCount('No suggestions');
                return;
            }

            setState('is-empty', false);
            var index = 0;
            var i;

            // SUGGESTIONS first, matching the design: completions are a way to narrow the query, and
            // a shopper who wants one wants it before scanning products. A site whose completion
            // corpus is not built sends none, and then no heading is emitted at all.
            if (suggestions.length) {
                panel.appendChild(sectionHeading('Suggestions', null));
                for (i = 0; i < suggestions.length; i++) {
                    var suggestion = suggestionRow(suggestions[i], index++, prefix);
                    panel.appendChild(suggestion);
                    options.push(suggestion);
                }
            }

            // FACETS next: a filtered search is the narrowest thing a prefix can mean, so it sits between
            // the completions and the products. A site whose engine sends none gets no heading at all.
            if (facets.length) {
                panel.appendChild(sectionHeading('Filters', null));
                for (i = 0; i < facets.length; i++) {
                    var facetOption = facetRow(facets[i], index++, prefix);
                    panel.appendChild(facetOption);
                    options.push(facetOption);
                }
            }

            if (products.length) {
                // The action is built BEFORE the heading it sits on, and pushed to options LAST, so
                // View All stays the final stop in the keyboard order however many sections precede it.
                var all = viewAllAction(index + products.length, prefix);
                panel.appendChild(sectionHeading('Products', all));
                for (i = 0; i < products.length; i++) {
                    var row = productRow(products[i], index++, prefix);
                    panel.appendChild(row);
                    options.push(row);
                }
                options.push(all);
            }

            showPanel();
            input.setAttribute('aria-expanded', 'true');
            setState('is-open', true);
            // Rows, not options: View All is an action on the products, not a result of its own.
            var rows = suggestions.length + facets.length + products.length;
            announceCount(rows + (rows === 1 ? ' suggestion' : ' suggestions') + ' available. Use the arrow keys to review.');
        }

        function fetchCompletions(prefix) {
            var cfgNow = config();
            if (!cfgNow) {
                return;
            }
            var thisRequest = ++requestId;
            setState('is-loading', true);
            try {
                var xhr = new window.XMLHttpRequest();
                var url = cfgNow.restBase + '/search/typeahead?q=' + window.encodeURIComponent(prefix)
                    + '&limit=' + window.encodeURIComponent(cfgNow.limit || 8);
                xhr.open('GET', url, true);
                xhr.withCredentials = true;
                xhr.onreadystatechange = function () {
                    if (xhr.readyState !== 4) {
                        return;
                    }
                    // Supersession: a response for anything but the newest request is discarded, not
                    // painted. Without this the slower of two in-flight requests wins by arriving last.
                    if (thisRequest !== requestId) {
                        return;
                    }
                    setState('is-loading', false);
                    try {
                        if (xhr.status < 200 || xhr.status >= 300) {
                            // A failed lookup closes the panel rather than showing stale suggestions.
                            close();
                            return;
                        }
                        var parsed = window.JSON.parse(xhr.responseText);
                        // parsed.content, NOT parsed.data -- WrapperDTO serializes as
                        // {messages, errors, content}. RestEnvelopeContractTest pins it.
                        //
                        // content is an OBJECT, not the bare product array it used to be. Both lists are
                        // defaulted rather than trusted, so an older monolith degrades to no rows.
                        var content = (parsed && parsed.content) || {};
                        render(content.products || [], content.suggestions || [], content.facets || [], prefix);
                    } catch (parseError) {
                        close();
                        logSuppressed(parseError);
                    }
                };
                xhr.send();
            } catch (sendError) {
                setState('is-loading', false);
                logSuppressed(sendError);
            }
        }

        input.addEventListener('input', function () {
            try {
                var prefix = input.value.trim();
                // A pending blur close belongs to the interaction just resumed: left to run it would
                // wipe the panel this keystroke is about to fill, and discard the request with it.
                if (blurTimer) {
                    window.clearTimeout(blurTimer);
                    blurTimer = null;
                }
                if (timer) {
                    window.clearTimeout(timer);
                }
                if (prefix.length < MIN_CHARS) {
                    // close() bumps requestId, so an in-flight response for a longer prefix cannot
                    // paint after the shopper has cleared the box back down to one character.
                    close();
                    return;
                }
                timer = window.setTimeout(function () {
                    fetchCompletions(prefix);
                }, DEBOUNCE_MS);
            } catch (error) {
                logSuppressed(error);
            }
        });

        // Choosing a suggestion RUNS the search, against the term rather than the product: the form
        // posts to the ordinary results page, so a selection is the same search the box degrades to
        // with JavaScript off and reports through the same search_results_viewed beacon.
        function runSearch(term) {
            input.value = term;
            // Before submitting: close() bumps requestId, so an in-flight request cannot repaint the
            // panel over the top of a page that is already navigating away.
            close();

            var form = input.form;
            if (!form) {
                // An overriding theme dropped the form. The value is in the box; degrade to that.
                return;
            }
            if (typeof form.requestSubmit === 'function') {
                // Preferred: it fires the form's submit listeners and validation, which
                // form.submit() silently skips.
                form.requestSubmit();
                return;
            }
            var submitButton = form.querySelector('[type="submit"]');
            if (submitButton) {
                // Same reason without requestSubmit: a real click dispatches the submit event.
                submitButton.click();
                return;
            }
            form.submit();
        }

        /**
         * One selection path for mouse and keyboard, so the two cannot drift apart.
         *
         * A product row with a usable link navigates to the product. A product whose link the URL guard
         * rejected searches its NAME rather than the typed prefix -- the shopper picked that product, so
         * the name is closer to what they chose, and it came from the engine's own corpus. The view-all
         * row, and a nameless product, search the typed prefix.
         */
        function selectOption(node) {
            var prefix = input.value.trim();
            var link = node.getAttribute('data-product-link');
            var productId = node.getAttribute('data-product-id');
            var suggestion = node.getAttribute('data-suggestion');
            var isViewAll = node.getAttribute('data-search-all') !== null;
            var isSuggestion = suggestion !== null;
            var facetGroup = node.getAttribute('data-facet-group');
            var isFacet = facetGroup !== null;

            // Announced BEFORE navigating, and before close(), because assigning location can tear the
            // page down before a listener runs. A conversion recorded slightly early beats one lost.
            var detail = {
                query: prefix,
                productId: (isViewAll || isSuggestion || isFacet) ? null : (productId || null),
                productName: (isViewAll || isSuggestion || isFacet) ? null : node.getAttribute('data-product-name'),
                // The completion the shopper chose, so a listener can tell a narrowed search from a
                // raw one. Null on every other row, which is what distinguishes the three kinds.
                suggestion: isSuggestion ? suggestion : null,
                position: options.indexOf(node),
                destination: (!isViewAll && !isSuggestion && !isFacet && link) ? 'product' : 'results',
            };
            if (isFacet) {
                // Added only here so every other row's payload is byte for byte what listeners already get.
                detail.facet = { group: facetGroup, value: node.getAttribute('data-facet-value') };
            }
            announceSelection(detail);

            if (isFacet) {
                var label = node.getAttribute('data-facet-label') || prefix;
                var base = searchUrl();
                if (!base) {
                    // No search page to navigate to (a template dropped the form): run the plain search.
                    runSearch(label);
                    return;
                }
                // k is the value's label: the search page needs a query, and the label is both what the shopper
                // picked and a term that the filter will then match. ix=1 stops the interpreter re-reading it.
                close();
                window.location.assign(base + (base.indexOf('?') === -1 ? '?' : '&') + 'k=' + window.encodeURIComponent(label)
                    + '&f.' + window.encodeURIComponent(facetGroup)
                    + '=' + window.encodeURIComponent(node.getAttribute('data-facet-value') || '')
                    + '&ix=1');
                return;
            }

            if (!isViewAll && !isSuggestion && link) {
                close();
                window.location.assign(link);
                return;
            }
            if (isSuggestion) {
                // The PHRASE, not the typed prefix: choosing "small posters" after typing "post" has
                // to search for what was chosen, or the completion did nothing.
                runSearch(suggestion);
                return;
            }
            runSearch(isViewAll ? prefix : (node.getAttribute('data-product-name') || prefix));
        }

        input.addEventListener('keydown', function (event) {
            try {
                if (panel.hidden && event.key !== 'Escape') {
                    return;
                }
                if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    highlight(activeIndex + 1 >= options.length ? 0 : activeIndex + 1);
                } else if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    highlight(activeIndex - 1 < 0 ? options.length - 1 : activeIndex - 1);
                } else if (event.key === 'Enter') {
                    // Only intercept Enter when a row is highlighted; otherwise the form submits, which
                    // is the ordinary search. The synthetic click routes through selectOption, so mouse
                    // and keyboard share one selection path.
                    if (activeIndex >= 0 && options[activeIndex]) {
                        event.preventDefault();
                        options[activeIndex].click();
                    }
                } else if (event.key === 'Escape') {
                    close();
                }
            } catch (error) {
                logSuppressed(error);
            }
        });

        // Delegated, one listener rather than one per row: rows are replaced on every keystroke, so
        // per-row listeners would be attached and discarded continuously.
        panel.addEventListener('click', function (event) {
            try {
                var node = event.target;
                while (node && node !== panel) {
                    if (node.getAttribute && node.getAttribute('data-typeahead-row') !== null) {
                        selectOption(node);
                        return;
                    }
                    node = node.parentNode;
                }
            } catch (error) {
                logSuppressed(error);
            }
        });

        input.addEventListener('blur', function () {
            // Deferred: a click on a row fires blur first, and closing immediately would remove the row
            // before its own click handler ran.
            blurTimer = window.setTimeout(function () {
                blurTimer = null;
                close();
            }, BLUR_CLOSE_MS);
        });
    }

    function safeInit() {
        try {
            init();
        } catch (error) {
            // A search box must never break the page it sits in.
            logSuppressed(error);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', safeInit);
    } else {
        safeInit();
    }
}(window, document));
